/**
 * Amazon Associates affiliate-link helpers.
 *
 * Purchase links MUST be built from a manually verified ASIN. If a book has no
 * verified ASIN, its purchase CTA must be disabled (see isPurchaseEnabled).
 *
 * The associate tag lives in an environment variable so it is never hard-coded:
 *   REACT_APP_AMAZON_ASSOCIATE_TAG=yourtag-20
 *
 * Review links are intentionally kept free of affiliate parameters. Amazon
 * requires that its review interface not be manipulated, so review URLs are
 * stored per-book, manually verified, and returned unchanged.
 */

export const AMAZON_ASSOCIATE_TAG = process.env.REACT_APP_AMAZON_ASSOCIATE_TAG || '';

const ASIN_PATTERN = /^[A-Z0-9]{10}$/;

/**
 * Returns true when an ASIN looks structurally valid. This does not verify
 * that the ASIN maps to the correct product; that verification is manual.
 */
export function isValidAsin(asin) {
    return typeof asin === 'string' && ASIN_PATTERN.test(asin.trim());
}

/**
 * Build a canonical Amazon product-detail affiliate URL from a verified ASIN.
 * Returns null when the ASIN is missing/invalid so the caller can disable the CTA.
 */
export function buildAmazonProductUrl(asin, marketplace = 'US') {
    if (!isValidAsin(asin)) return null;

    // Only the US marketplace is currently supported.
    const domain = marketplace === 'US' ? 'www.amazon.com' : 'www.amazon.com';
    const base = `https://${domain}/dp/${asin.trim()}`;
    return AMAZON_ASSOCIATE_TAG ? `${base}?tag=${encodeURIComponent(AMAZON_ASSOCIATE_TAG)}` : base;
}

/**
 * Resolve the purchase URL for a book record.
 * Prefers an explicitly provided amazonProductUrl, otherwise derives it from
 * the ASIN. Returns null when nothing verified is available.
 */
export function getPurchaseUrl(book) {
    if (!book) return null;
    if (book.amazonProductUrl) {
        // Respect an explicitly configured, manually verified URL as-is.
        return book.amazonProductUrl;
    }
    return buildAmazonProductUrl(book.amazonAsin, book.amazonMarketplace || 'US');
}

/**
 * A purchase CTA is only enabled when the book is published AND we can build a
 * verified product URL. Coming-soon and unverified titles never link out.
 */
export function isPurchaseEnabled(book) {
    if (!book) return false;
    if (book.status !== 'published') return false;
    return Boolean(getPurchaseUrl(book));
}

/**
 * Review URLs are returned unchanged and MUST NOT carry affiliate parameters.
 */
export function getReviewUrl(book) {
    if (!book || !book.amazonReviewUrl) return null;
    return book.amazonReviewUrl;
}

const amazonHelpers = {
    AMAZON_ASSOCIATE_TAG,
    isValidAsin,
    buildAmazonProductUrl,
    getPurchaseUrl,
    isPurchaseEnabled,
    getReviewUrl,
};

export default amazonHelpers;
